import PropTypes from 'prop-types'

// Common item/block shape used across components
export const BlockItemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  color: PropTypes.string,
  isSeparated: PropTypes.bool,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  details: PropTypes.array
})

// Formats object structure for different formatting types
export const FormatsShape = PropTypes.shape({
  // Block formatting by depth or single format
  blocks: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // Tooltip formatting
  tooltips: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // Label formatting
  labels: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // Hover details formatting
  hoverDetails: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // List items formatting
  listItems: PropTypes.string,
  // Summary value formatting
  summaryValue: PropTypes.string,
  // Group value formatting
  groupValue: PropTypes.string,
  // Item value formatting
  itemValue: PropTypes.string
})

// Shared ClassNames structure
export const BaseClassNamesShape = PropTypes.shape({
  // Main container classes
  container: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // Block-specific classes by depth
  blocks: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // Label classes
  labels: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // Empty state classes
  emptyState: PropTypes.string
})

// VisualBlocks specific ClassNames
export const VisualBlocksClassNamesShape = PropTypes.shape({
  ...BaseClassNamesShape.type,
  // Avatar classes
  avatar: PropTypes.string,
  // Tree branch classes
  treeBranch: PropTypes.string
})

// VisualBlockList specific ClassNames
export const VisualBlockListClassNamesShape = PropTypes.shape({
  ...BaseClassNamesShape.type,
  // List item classes
  listItems: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // List item list classes
  listItemList: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ]),
  // List item container classes
  listItemContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ])
})

// Generic ClassNames shape for shared usage
export const ClassNamesShape = BaseClassNamesShape

// Shared Styles structure
export const BaseStylesShape = PropTypes.shape({
  // Main container styles
  container: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // Block-specific styles by depth
  blocks: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // Row styles by depth
  rows: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // Column styles by depth
  columns: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // Label styles
  labels: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // Empty state styles
  emptyState: PropTypes.object
})

// VisualBlocks specific Styles
export const VisualBlocksStylesShape = PropTypes.shape({
  ...BaseStylesShape.type,
  // Avatar styles
  avatar: PropTypes.object,
  // Tree branch styles
  treeBranch: PropTypes.object
})

// VisualBlockList specific Styles
export const VisualBlockListStylesShape = PropTypes.shape({
  ...BaseStylesShape.type,
  // List item styles
  listItems: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // List item list styles
  listItemList: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object)
  ]),
  // List item container styles
  listItemContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.string)
  ])
})

// Generic Styles shape for shared usage
export const StylesShape = BaseStylesShape

// Separated groups filter shape
export const SeparatedGroupShape = PropTypes.shape({
  accessor: PropTypes.string.isRequired,
  op: PropTypes.oneOf([
    'eq',
    'neq',
    'lt',
    'lte',
    'gt',
    'gte',
    'in',
    'nin',
    'contains',
    'ncontains'
  ]).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array
  ]).isRequired
})

// Common props for both VisualBlocks and VisualBlockList
export const CommonVisualBlocksProps = {
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,
  dataAccessor: PropTypes.string,
  nameAccessor: PropTypes.string,
  valueAccessor: PropTypes.string,
  colorAccessor: PropTypes.string,
  detailsAccessor: PropTypes.string,
  orderAccessor: PropTypes.string,
  colors: PropTypes.arrayOf(PropTypes.string),
  enableManualAccountManagement: PropTypes.bool,
  separatedGroups: PropTypes.arrayOf(SeparatedGroupShape),
  hideZeroValueAccounts: PropTypes.bool,
  debug: PropTypes.bool,
  formats: FormatsShape,
  classNames: ClassNamesShape,
  styles: StylesShape
}

// VisualBlocks specific props
export const VisualBlocksProps = {
  ...CommonVisualBlocksProps,
  includeAvatar: PropTypes.bool,
  maxBlockDepth: PropTypes.number,
  classNames: VisualBlocksClassNamesShape,
  styles: VisualBlocksStylesShape
}

// VisualBlockList specific props
export const VisualBlockListProps = {
  ...CommonVisualBlocksProps,
  maxListDepth: PropTypes.number,
  classNames: VisualBlockListClassNamesShape,
  styles: VisualBlockListStylesShape
}

// Label size options
export const LabelSizeShape = PropTypes.oneOf(['sm', 'md', 'lg'])

// Label location options
export const LabelLocationShape = PropTypes.oneOf(['inside', 'outside'])
